'use client';

import Link, { type LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import { type AnchorHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type NavLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> &
  Pick<LinkProps, 'href' | 'prefetch'> & {
    children: ReactNode;
    /** Extra class names applied only when the link is active (inert). */
    activeClassName?: string;
  };

/**
 * Link that renders as an inert <span> when its href matches the current
 * pathname. Avoids self-referencing links — better for SEO crawl, screen
 * readers (announces `aria-current="page"`), and visual UX (no hover cursor).
 *
 * Only matches by pathname; query strings and hashes are ignored. Pure
 * hash links (e.g. `/#contact`) stay active everywhere except home.
 */
export function NavLink({
  href,
  className,
  activeClassName,
  children,
  prefetch,
  ...rest
}: NavLinkProps) {
  const pathname = usePathname() ?? '/';
  const hrefString =
    typeof href === 'string'
      ? href
      : (href.pathname ?? '');
  const targetPath = hrefString.split('#')[0]?.split('?')[0] ?? '';
  const normalized = targetPath === '' ? pathname : targetPath;
  const isSelf = normalized === pathname;

  if (isSelf) {
    // A <span> has no role that supports disclosure state, so the trigger's
    // `aria-expanded` / `aria-controls` would be invalid ARIA here (axe
    // aria-allowed-attr, WCAG 4.1.2). The panel's own links still open it via
    // focus-within, so dropping the state loses nothing for keyboard users.
    const {
      'aria-expanded': _expanded,
      'aria-controls': _controls,
      ...spanProps
    } = rest;
    void _expanded;
    void _controls;
    return (
      <span
        {...spanProps}
        aria-current="page"
        className={cn('cursor-default select-none', className, activeClassName)}
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      {...rest}
      href={href}
      {...(prefetch !== undefined && { prefetch })}
      className={cn(className)}
    >
      {children}
    </Link>
  );
}
